import { ChevronDownIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, Portal, Text, useToast } from "@chakra-ui/react";
import { Sender } from "@ant-design/x";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";

import GenImageButton from "./gen_image_button";
import { imageUploaded } from "@/services/api/thunks/image";
import { selectUser } from "@/reducer/userslice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { GenImageSubmitPayload } from "@/types/design";
import { selectMode } from "@/reducer/modeslice";
const SMART_RESOLUTION_KEY = "auto";
const SMART_RATIO_KEY = "auto";
const SENDER_ROOT_CLASS = "design-prompt-sender";
const MAX_REFERENCE_IMAGES = 9;
const PRO_MODEL_TIER_KEY = "pro";
const ADV_MODEL_TIER_KEY = "advanced";
const ADVANCED_MODEL_BASE_PRICE = 4;

const MODEL_TIER_OPTIONS = [
  { key: "pro", label: "专业版", realname: "gemini-2.5-flash-image" },
  { key: "advanced", label: "高阶版", realname: "gemini-3-pro-image-preview" },
  { key: "flagship", label: "旗舰版", realname: "gpt-image-2" },
] as const;

const RESOLUTION_OPTIONS = [
  { key: SMART_RESOLUTION_KEY, label: "智能分辨率" },
  { key: "1K", label: "1K" },
  { key: "2K", label: "2K" },
  { key: "4K", label: "4K" },
] as const;

const RATIO_OPTIONS = [
  { key: SMART_RATIO_KEY, label: "智能比例" },
  { key: "1:1", label: "1:1" },
  { key: "1:2", label: "1:2" },
  { key: "2:1", label: "2:1" },
  { key: "4:3", label: "4:3" },
  { key: "3:4", label: "3:4" },
  { key: "16:9", label: "16:9" },
  { key: "9:16", label: "9:16" },
  { key: "21:9", label: "21:9" },
] as const;

const RESOLUTION_PRICE_MAP: Record<string, number> = {
  [SMART_RESOLUTION_KEY]: ADVANCED_MODEL_BASE_PRICE,
  "1k": 4,
  "2k": 6,
  "4k": 8,
};

const CONTROL_MENU_STYLES = {
  h: "36px",
  px: "12px",
  bg: "rgba(255, 255, 255, 0.06)",
  color: "#FFFFFF",
  borderColor: "#27314D",
  borderRadius: "10px",
  fontSize: "13px",
  fontWeight: 500,
  _hover: { borderColor: "#496AE1", bg: "rgba(255, 255, 255, 0.08)" },
  _active: { bg: "rgba(255, 255, 255, 0.1)" },
  _focus: { borderColor: "#496AE1", boxShadow: "0 0 0 1px #496AE1" },
};

type SenderMenuOption = {
  label: string;
  value: string;
};

type SenderMenuProps = {
  width: string;
  value: string;
  valueLabel: string;
  options: SenderMenuOption[];
  onSelect: (value: string) => void;
};

const SenderMenu = ({ width, value, valueLabel, options, onSelect }: SenderMenuProps) => (
  <Menu matchWidth placement="top-start" gutter={8}>
    <MenuButton as={Button} size="sm" w={width} minW={width} flex="0 0 auto" rightIcon={<ChevronDownIcon />} justifyContent="space-between" textAlign="left" iconSpacing="8px" {...CONTROL_MENU_STYLES}>
      <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {valueLabel}
      </Box>
    </MenuButton>
    <Portal>
      <MenuList minW={width} p="6px" bg="#13151B" borderColor="#27314D" borderRadius="12px" boxShadow="0 12px 32px rgba(0, 0, 0, 0.32)" color="#FFFFFF" zIndex={20}>
        {options.map((item) => {
          const isActive = item.value === value;

          return (
            <MenuItem key={item.value} minH="34px" borderRadius="10px" bg={isActive ? "rgba(73, 106, 225, 0.16)" : "transparent"} color="#FFFFFF" fontSize="13px" _hover={{ bg: isActive ? "rgba(73, 106, 225, 0.22)" : "#27314D" }} _focus={{ bg: isActive ? "rgba(73, 106, 225, 0.22)" : "#27314D" }} onClick={() => onSelect(item.value)}>
              <Flex w="100%" align="center" justify="space-between" gap="8px">
                <Box>{item.label}</Box>
                {isActive && <Box w="6px" h="6px" borderRadius="999px" bg="#7AA2FF" flex="0 0 auto" />}
              </Flex>
            </MenuItem>
          );
        })}
      </MenuList>
    </Portal>
  </Menu>
);

const PromptTextArea = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const user = useAppSelector(selectUser);
  const mode = useAppSelector(selectMode);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [selectedModelTier, setSelectedModelTier] = useState<string>(PRO_MODEL_TIER_KEY);
  const [selectedResolution, setSelectedResolution] = useState<string>(SMART_RESOLUTION_KEY);
  const [selectedRatio, setSelectedRatio] = useState<string>(SMART_RATIO_KEY);
  const [referenceImages, setReferenceImages] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const tieredModelOptions = MODEL_TIER_OPTIONS;

  const selectedModel = useMemo(() => tieredModelOptions.find((item) => item.key === selectedModelTier), [selectedModelTier, tieredModelOptions]);

  const selectedModelLabel = tieredModelOptions.find((item) => item.key === selectedModelTier)?.label || MODEL_TIER_OPTIONS[0].label;

  const selectedResolutionLabel = RESOLUTION_OPTIONS.find((item) => item.key === selectedResolution)?.label || RESOLUTION_OPTIONS[0].label;

  const selectedRatioLabel = RATIO_OPTIONS.find((item) => item.key === selectedRatio)?.label || RATIO_OPTIONS[0].label;

  const displayCoin = useMemo(() => {
    if (selectedModelTier === PRO_MODEL_TIER_KEY) {
      return 2;
    }

    return RESOLUTION_PRICE_MAP[selectedResolution] ?? ADVANCED_MODEL_BASE_PRICE;
  }, [selectedModelTier, selectedResolution]);

  const submitParams = useMemo<GenImageSubmitPayload>(
    () => ({
      prompt: inputValue,
      modelTier: selectedModelTier,
      modelLabel: selectedModelLabel,
      modelRealname: selectedModel?.realname || "",
      resolution: selectedModelTier === PRO_MODEL_TIER_KEY || selectedModelTier === ADV_MODEL_TIER_KEY ? "1K" : selectedResolution,
      ratio: selectedRatio,
      totalCoin: displayCoin,
      files: referenceImages,
    }),
    [displayCoin, inputValue, referenceImages, selectedModel, selectedModelLabel, selectedModelTier, selectedRatio, selectedResolution],
  );

  const modelMenuOptions = tieredModelOptions.map((item) => ({
    label: item.label,
    value: item.key,
  }));

  const resolutionMenuOptions = RESOLUTION_OPTIONS.map((item) => ({
    label: item.label,
    value: item.key,
  }));

  const ratioMenuOptions = RATIO_OPTIONS.map((item) => ({
    label: item.label,
    value: item.key,
  }));
  useEffect(() => {
    setReferenceImages(mode.files);
  }, [mode.files]);
  const handleSenderChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleModelChange = useCallback((tierKey: string) => {
    setSelectedModelTier(tierKey);
  }, []);

  const handleResolutionChange = useCallback((resolutionKey: string) => {
    setSelectedResolution(resolutionKey);
    // 当模型为旗舰版且选择具体分辨率时，将比例设置为"1:1"（而不是"智能比例"）
    if (selectedModelTier === "flagship" && resolutionKey !== SMART_RESOLUTION_KEY) {
      setSelectedRatio("1:1");
    }
  }, [selectedModelTier]);

  const handleRatioChange = useCallback((ratioKey: string) => {
    setSelectedRatio(ratioKey);
  }, []);

  const handleClickChooseSystemFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveSelectedImage = useCallback((targetIndex: number) => {
    setReferenceImages((prev) => prev.filter((_, index) => index !== targetIndex));
  }, []);

  const handleClearSelectedImages = useCallback(() => {
    setReferenceImages([]);
    setPreviewImage(null);
  }, []);

  const handleOpenPreviewImage = useCallback((imageUrl: string) => {
    setPreviewImage(imageUrl);
  }, []);

  const handleClosePreviewImage = useCallback(() => {
    setPreviewImage(null);
  }, []);

  const handlePickSystemFiles = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      event.target.value = "";

      if (!files.length) {
        return;
      }

      if (!user.id) {
        toast({
          title: "请先登录",
          description: "登录后才可以上传系统文件",
          status: "warning",
          duration: 1600,
          position: "top",
        });
        return;
      }

      const availableSlots = MAX_REFERENCE_IMAGES - referenceImages.length;

      if (availableSlots <= 0) {
        toast({
          title: "最多只能选择9张图片",
          status: "warning",
          duration: 1800,
          position: "top",
        });
        return;
      }

      const filesToUpload = files.slice(0, availableSlots);
      const nextReferenceImages = [...referenceImages];
      let successCount = 0;
      let failedCount = 0;

      for (const file of filesToUpload) {
        const result = await dispatch(
          imageUploaded({
            file,
            image_category: "user",
            is_intermediate: true,
            imageType: "STRUCTURE_IMAGE",
            session_id: user.id,
          }),
        );

        if (!imageUploaded.fulfilled.match(result)) {
          failedCount += 1;
          continue;
        }

        const payload = result.payload as { image_url: string };
        nextReferenceImages.push(payload.image_url);
        successCount += 1;
      }

      if (successCount > 0) {
        setReferenceImages(nextReferenceImages);
        toast({
          title: `已添加 ${successCount} 张图片`,
          description: files.length > availableSlots ? `最多保留 ${MAX_REFERENCE_IMAGES} 张，已自动截取前 ${availableSlots} 张` : undefined,
          status: failedCount > 0 ? "warning" : "success",
          duration: 1600,
          position: "top",
        });
        return;
      }

      toast({
        title: "文件上传失败",
        status: "error",
        duration: 1800,
        position: "top",
      });
    },
    [dispatch, referenceImages, toast, user.id],
  );

  const senderHeader =
    referenceImages.length > 0 ? (
      <Flex gap="10px" wrap="wrap" align="center" px="12px" py="12px" bg="rgba(255, 255, 255, 0.03)" borderBottom="1px solid rgba(73, 106, 225, 0.18)">
        <Text flex="0 0 auto" fontSize="12px" fontWeight={500} color="rgba(255, 255, 255, 0.72)">
          已选图片
        </Text>
        <Button variant="unstyled" ml="auto" minW="auto" h="20px" fontSize="12px" fontWeight={500} color="rgba(122, 162, 255, 0.92)" _hover={{ color: "#AFC6FF" }} _active={{ color: "#D7E4FF" }} onClick={handleClearSelectedImages}>
          清空全部
        </Button>
        <Flex w="100%" gap="8px" wrap="wrap">
          {referenceImages.map((item, index) => (
            <Box
              key={`${item}-${index}`}
              w="52px"
              h="52px"
              position="relative"
              role="group"
              overflow="hidden"
              borderRadius="12px"
              border="1px solid #27314D"
              bg="rgba(255, 255, 255, 0.04)"
              boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.04)"
              cursor="zoom-in"
              transition="border-color 0.2s ease, transform 0.2s ease"
              _hover={{ borderColor: "#496AE1", transform: "translateY(-1px)" }}
              onClick={() => handleOpenPreviewImage(item)}
            >
              <Image src={item} alt={`selected-reference-${index}`} w="100%" h="100%" objectFit="cover" />
              <Flex
                position="absolute"
                inset="0"
                align="center"
                justify="center"
                bg="rgba(0, 0, 0, 0.18)"
                color="#FFFFFF"
                fontSize="11px"
                fontWeight={500}
                opacity={0}
                transition="opacity 0.2s ease"
                _groupHover={{ opacity: 1 }}
                pointerEvents="none"
              >
                查看
              </Flex>
              <IconButton
                aria-label={`remove-selected-reference-${index}`}
                icon={
                  <Text fontSize="12px" lineHeight="1">
                    ×
                  </Text>
                }
                size="xs"
                minW="18px"
                w="18px"
                h="18px"
                position="absolute"
                top="4px"
                right="4px"
                borderRadius="999px"
                bg="rgba(0, 0, 0, 0.58)"
                color="#FFFFFF"
                _hover={{ bg: "rgba(0, 0, 0, 0.76)" }}
                _active={{ bg: "rgba(0, 0, 0, 0.84)" }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveSelectedImage(index);
                }}
              />
            </Box>
          ))}
        </Flex>
      </Flex>
    ) : undefined;

  return (
    <Box mt="10px">
      <Sender
        rootClassName={SENDER_ROOT_CLASS}
        submitType="shiftEnter"
        value={inputValue}
        placeholder="请输入提示词"
        onChange={handleSenderChange}
        suffix={false}
        header={senderHeader}
        styles={{
          root: {
            color: "#FFFFFF",
            background: "linear-gradient(180deg, rgba(26, 28, 35, 0.96) 0%, rgba(18, 20, 26, 0.98) 100%)",
            borderColor: "#27314D",
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.03)",
          },
          content: {
            color: "#FFFFFF",
            backgroundColor: "transparent",
            padding: "10px 14px 14px",
          },
          input: {
            color: "#FFFFFF",
            caretColor: "#FFFFFF",
            backgroundColor: "transparent",
            fontSize: "14px",
            lineHeight: 1.7,
            minHeight: "104px",
            padding: 0,
          },
        }}
        footer={
          <Flex gap="8px" wrap="wrap" align="center" p="10px 12px" bg="rgba(255, 255, 255, 0.03)" borderBottom="1px solid rgba(73, 106, 225, 0.18)" backdropFilter="blur(10px)">
            <SenderMenu width="136px" value={selectedModelTier} valueLabel={selectedModelLabel} options={modelMenuOptions} onSelect={handleModelChange} />

            <Button size="sm" h="36px" px="14px" color="#FFFFFF" bg="rgba(255, 255, 255, 0.06)" border="1px solid #27314D" borderRadius="10px" fontSize="13px" fontWeight={500} _hover={{ bg: "rgba(255, 255, 255, 0.08)", borderColor: "#496AE1" }} _active={{ bg: "rgba(255, 255, 255, 0.1)" }} onClick={handleClickChooseSystemFile}>
              选择系统文件
            </Button>

            <SenderMenu width="156px" value={selectedResolution} valueLabel={selectedResolutionLabel} options={resolutionMenuOptions} onSelect={handleResolutionChange} />

            {(selectedModelTier === "flagship" && selectedResolution !== SMART_RESOLUTION_KEY) && <SenderMenu width="148px" value={selectedRatio} valueLabel={selectedRatioLabel} options={ratioMenuOptions.filter(option => option.value !== SMART_RATIO_KEY)} onSelect={handleRatioChange} />}
            {selectedModelTier !== "flagship" && <SenderMenu width="148px" value={selectedRatio} valueLabel={selectedRatioLabel} options={ratioMenuOptions} onSelect={handleRatioChange} />}

            <Box ml={{ base: "0", md: "auto" }}>
              <GenImageButton totalCoin={displayCoin} submitParams={submitParams} variant="inline" />
            </Box>
          </Flex>
        }
        style={{
          width: "100%",
          backgroundColor: "transparent",
          borderColor: "#27314D",
          color: "#FFFFFF",
        }}
      />

      <style>
        {`
          .${SENDER_ROOT_CLASS} {
            transition: border-color 0.2s ease, box-shadow 0.2s ease,
              transform 0.2s ease;
          }

          .${SENDER_ROOT_CLASS}:hover {
            border-color: #33456f !important;
          }

          .${SENDER_ROOT_CLASS}:focus-within {
            border-color: #496ae1 !important;
            box-shadow:
              0 0 0 1px rgba(73, 106, 225, 0.65),
              0 14px 34px rgba(0, 0, 0, 0.22),
              inset 0 1px 0 rgba(255, 255, 255, 0.04) !important;
          }

          .${SENDER_ROOT_CLASS} .ant-sender-content {
            gap: 10px;
          }

          .${SENDER_ROOT_CLASS} textarea,
          .${SENDER_ROOT_CLASS} .ant-input,
          .${SENDER_ROOT_CLASS} .ant-input-textarea textarea {
            color: #ffffff !important;
            -webkit-text-fill-color: #ffffff !important;
            caret-color: #ffffff !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .${SENDER_ROOT_CLASS} textarea {
            resize: none !important;
          }

          .${SENDER_ROOT_CLASS} textarea::placeholder,
          .${SENDER_ROOT_CLASS} .ant-input::placeholder,
          .${SENDER_ROOT_CLASS} .ant-input-textarea textarea::placeholder {
            color: rgba(255, 255, 255, 0.45) !important;
            -webkit-text-fill-color: rgba(255, 255, 255, 0.45) !important;
          }

          .${SENDER_ROOT_CLASS} .ant-input-textarea-show-count::after {
            color: rgba(255, 255, 255, 0.32) !important;
          }
        `}
      </style>

      <input ref={fileInputRef} type="file" multiple accept="image/png,image/jpeg,image/jpg" style={{ display: "none" }} onChange={handlePickSystemFiles} />

      <Modal isOpen={Boolean(previewImage)} onClose={handleClosePreviewImage} isCentered size="4xl">
        <ModalOverlay bg="rgba(0, 0, 0, 0.72)" backdropFilter="blur(6px)" />
        <ModalContent bg="#0C0B10" border="1px solid #27314D" borderRadius="20px" boxShadow="0 24px 64px rgba(0, 0, 0, 0.42)">
          <ModalCloseButton color="rgba(255, 255, 255, 0.72)" _hover={{ color: "#FFFFFF", bg: "rgba(255, 255, 255, 0.08)" }} />
          <ModalBody p="16px">
            {previewImage && (
              <Image
                src={previewImage}
                alt="reference-preview"
                w="100%"
                maxH="78vh"
                objectFit="contain"
                borderRadius="14px"
                bg="rgba(255, 255, 255, 0.03)"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PromptTextArea;
